import React, { useState } from 'react'
import {
  Box,
  Button,
  Divider,
  Popover,
  Stack,
  Switch,
  Typography,
  useTheme
} from '@mui/material'
import GroupsIcon from '@mui/icons-material/Groups'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import CarCrashOutlinedIcon from '@mui/icons-material/CarCrashOutlined'
import { Trans } from '@lingui/macro'
import { maplibreGroupChildButtonSx } from '../../consts/map-floating-control-sx'
import SvgCustomIcon from '../icons/SvgCustomIcon'

const ROW_ICONS = {
  publicCrashes: CarCrashOutlinedIcon,
  safetyIssues: ReportProblemOutlinedIcon
}

function getRowIcon(elem) {
  return ROW_ICONS[elem.name] || GroupsIcon
}

const CommunityMapButton = React.memo(({ communitySwitches, setCommunitySwitches }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const theme = useTheme()

  const isLight = theme.palette.mode === 'light'
  const textPrimary = isLight
    ? theme.palette.newColors?.gray || theme.palette.text.primary
    : theme.palette.newColors?.lightGray || theme.palette.text.primary
  const textSecondary = theme.palette.text.secondary
  const surfaceBg = isLight ? theme.palette.background.paper : theme.palette.background.default
  const rowBorder = theme.palette.divider
  const rowHoverBg = isLight ? 'rgba(99, 102, 241, 0.04)' : 'rgba(255, 255, 255, 0.04)'
  const activeRowBg = isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.16)'

  const handleButtonClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'community-button-popover' : undefined

  return (
    <>
      <Button
        type="button"
        className="fred-map-toolbar-btn"
        aria-describedby={id}
        onClick={handleButtonClick}
        sx={maplibreGroupChildButtonSx(theme)}
      >
        <SvgCustomIcon name="twoPerson" color="#425B61" size={24} />
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              borderRadius: 'var(--radius-2xl, 12px)',
              boxShadow: theme.shadows[8],
              overflow: 'hidden',
              minWidth: 320,
              maxWidth: 400
            }
          }
        }}
      >
        <Box
          id="community-popover-box"
          onClick={(e) => e.stopPropagation()}
          sx={{
            bgcolor: surfaceBg,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${rowBorder}`,
              bgcolor: isLight ? 'var(--color-background, #F8FAFC)' : 'transparent'
            }}
          >
            <GroupsIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
            <Typography
              sx={{
                flex: 1,
                fontSize: 'var(--font-lg, 14px)',
                fontWeight: 700,
                color: textPrimary
              }}
            >
              <Trans>Community</Trans>
            </Typography>
          </Stack>

          <Box sx={{ px: 1.5, py: 1.5 }}>
            {communitySwitches.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ px: 1, py: 2, textAlign: 'center', color: textSecondary }}
              >
                <Trans>No community layers available</Trans>
              </Typography>
            ) : (
              <Stack spacing={1} divider={<Divider flexItem />}>
                {communitySwitches.map((elem, index) => {
                  const RowIcon = getRowIcon(elem)
                  const rowKey = `${elem.name}-${index}`

                  return (
                    <Box
                      key={rowKey}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.25,
                        borderRadius: 'var(--radius-lg, 8px)',
                        border: `1px solid ${elem.checked ? theme.palette.primary.main : rowBorder}`,
                        bgcolor: elem.checked ? activeRowBg : 'transparent',
                        transition: 'background-color 0.15s ease, border-color 0.15s ease',
                        '&:hover': {
                          bgcolor: elem.checked ? activeRowBg : rowHoverBg
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 'var(--radius-md, 6px)',
                          bgcolor: isLight ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.2)',
                          color: theme.palette.primary.main
                        }}
                      >
                        <RowIcon sx={{ fontSize: 20 }} />
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontWeight: 600,
                          fontSize: 'var(--font-md, 13px)',
                          color: textPrimary,
                          lineHeight: 1.35
                        }}
                      >
                        {elem.label}
                      </Typography>

                      <Switch
                        size="small"
                        checked={Boolean(elem.checked)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const checked = e.target.checked
                          const next = communitySwitches.map((item, i) =>
                            i === index ? { ...item, checked } : item
                          )
                          setCommunitySwitches(next)
                          elem.action(checked)
                        }}
                        sx={{
                          flexShrink: 0,
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: theme.palette.primary.main
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: theme.palette.primary.main
                          }
                        }}
                      />
                    </Box>
                  )
                })}
              </Stack>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  )
})

export default CommunityMapButton
